import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { History, Loader2, Sparkles, ArrowRight, GitCompare, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ScanRequiredEmpty, ScoreBar, prettyEnum } from "./beautyUi";
import { setLatestBeautyScan } from "./useLatestBeautyScan";

export default function BeautyHistory() {
  const navigate = useNavigate();
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(null); // scan id being deleted, or "all"

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/beauty/scans");
        setScans(data.scans || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const deleteOne = async (scanId) => {
    if (!window.confirm("Delete this scan? This can't be undone.")) return;
    setBusy(scanId);
    try {
      await api.delete(`/beauty/scans/${scanId}`);
      setScans((s) => s.filter((x) => x.id !== scanId));
      // Reset the shared cache — makeup / hair-beard / care will refetch
      // the next-most-recent scan (or show "no scan" if none).
      setLatestBeautyScan(null);
      toast.success("Scan deleted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete the scan.");
    } finally { setBusy(null); }
  };

  const deleteAll = async () => {
    if (!window.confirm(`Delete ALL ${scans.length} scans? This can't be undone.`)) return;
    setBusy("all");
    try {
      const { data } = await api.delete("/beauty/scans");
      setScans([]);
      setLatestBeautyScan(null);
      toast.success(`${data.deleted || 0} scans deleted.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't clear history.");
    } finally { setBusy(null); }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length === 2) {
        toast.info("Deselect one first — Compare works with exactly two scans.");
        return prev;
      }
      return [...prev, id];
    });
  };

  const openCompare = () => {
    if (selected.length !== 2) return;
    // Order chronologically: earlier → newer
    const map = new Map(scans.map(s => [s.id, new Date(s.created_at).getTime()]));
    const [a, b] = [...selected].sort((x, y) => map.get(x) - map.get(y));
    navigate(`/beauty/compare?a=${a}&b=${b}`);
  };

  const cancelCompare = () => { setCompareMode(false); setSelected([]); };

  const canCompare = useMemo(() => scans.length >= 2, [scans]);

  if (loading) return <div className="max-w-5xl mx-auto p-16 text-center"><Loader2 className="mx-auto animate-spin" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-14 pb-32" data-testid="beauty-history-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow flex items-center gap-2"><History size={12} /> AI History</p>
          <h1 className="editorial-h1 mt-3">Your beauty journey.</h1>
        </div>
        {canCompare && (
          compareMode ? (
            <button
              type="button"
              onClick={cancelCompare}
              data-testid="beauty-history-compare-cancel"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] underline underline-offset-4 text-muted-foreground"
            >
              <X size={13} /> Cancel compare
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCompareMode(true)}
                data-testid="beauty-history-compare-toggle"
                className="inline-flex items-center gap-2 border border-border px-4 h-10 text-[11px] uppercase tracking-[0.2em] hover:border-foreground"
              >
                <GitCompare size={13} /> Compare mode
              </button>
              <button
                type="button"
                onClick={deleteAll}
                disabled={busy === "all"}
                data-testid="beauty-history-delete-all"
                className="inline-flex items-center gap-2 border border-destructive/40 text-destructive px-4 h-10 text-[11px] uppercase tracking-[0.2em] hover:border-destructive disabled:opacity-60"
              >
                {busy === "all" ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                Delete all
              </button>
            </div>
          )
        )}
        {!canCompare && scans.length > 0 && (
          <button
            type="button"
            onClick={deleteAll}
            disabled={busy === "all"}
            data-testid="beauty-history-delete-all"
            className="inline-flex items-center gap-2 border border-destructive/40 text-destructive px-4 h-10 text-[11px] uppercase tracking-[0.2em] hover:border-destructive disabled:opacity-60"
          >
            {busy === "all" ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            Delete history
          </button>
        )}
      </div>
      <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
        {compareMode ? "Tap two scan cards to compare progress side-by-side." : "Every scan you've run — tap one to revisit the full report."}
      </p>

      {scans.length === 0 && (
        <div className="mt-8"><ScanRequiredEmpty title="No scans yet" desc="Run your first AI Scan — your history and progress will appear here." /></div>
      )}

      {scans.length > 0 && (
        <ul className="mt-10 grid sm:grid-cols-2 gap-4" data-testid="beauty-history-list">
          {scans.map((s) => {
            const face = s.analysis?.face || {};
            const scores = s.analysis?.overall_scores || {};
            const d = new Date(s.created_at);
            const date = d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
            const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
            const isSelected = selected.includes(s.id);
            const Card = (
              <div
                className={`block border p-5 transition ${compareMode
                  ? `${isSelected ? "border-accent bg-accent/5" : "border-border hover:border-foreground"} cursor-pointer`
                  : "border-border hover:border-foreground"}`}
              >
                <div className="flex items-baseline justify-between gap-4">
                  <div>
                    <p className="eyebrow flex items-center gap-2"><Sparkles size={11} /> {date}</p>
                    <p className="font-serif text-2xl mt-1">{prettyEnum(face.face_shape)}</p>
                    <p className="text-xs text-muted-foreground mt-1">Tone · {prettyEnum(face.skin_tone)} · Undertone · {prettyEnum(face.skin_undertone)}</p>
                  </div>
                  {compareMode ? (
                    <div
                      className={`h-5 w-5 border ${isSelected ? "bg-accent border-accent" : "border-foreground/40"} flex items-center justify-center`}
                      data-testid={`beauty-history-select-${s.id}`}
                    >
                      {isSelected && <span className="text-background text-[10px]">✓</span>}
                    </div>
                  ) : (
                    <ArrowRight size={16} className="opacity-60" />
                  )}
                </div>
                <div className="mt-4 space-y-3">
                  <ScoreBar label="Skin" value={scores.skin_health} testId={`hist-skin-${s.id}`} />
                  <ScoreBar label="Hair" value={scores.hair_health} testId={`hist-hair-${s.id}`} />
                </div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-4">Scanned at {time}</p>
              </div>
            );
            return (
              <li key={s.id} className="relative">
                {compareMode ? (
                  <button
                    type="button"
                    onClick={() => toggleSelect(s.id)}
                    className="text-left w-full"
                    data-testid={`beauty-history-card-${s.id}`}
                  >{Card}</button>
                ) : (
                  <Link to={`/beauty/scans/${s.id}`} data-testid={`beauty-history-item-${s.id}`}>{Card}</Link>
                )}
                {!compareMode && (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteOne(s.id); }}
                    disabled={busy === s.id}
                    data-testid={`beauty-history-delete-${s.id}`}
                    aria-label="Delete scan"
                    className="absolute top-2 right-2 h-8 w-8 inline-flex items-center justify-center bg-background/90 border border-border hover:border-destructive hover:text-destructive transition-colors disabled:opacity-50"
                  >
                    {busy === s.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {compareMode && selected.length > 0 && (
        <div className="fixed bottom-24 lg:bottom-8 left-1/2 -translate-x-1/2 z-40 border border-foreground bg-background shadow-lg px-4 py-3 flex items-center gap-3" data-testid="beauty-history-compare-cta">
          <p className="text-xs uppercase tracking-[0.18em]">{selected.length} / 2 selected</p>
          <Button
            disabled={selected.length !== 2}
            onClick={openCompare}
            className="rounded-none uppercase tracking-[0.2em] text-[11px] h-9"
            data-testid="beauty-history-compare-open"
          >
            <GitCompare size={13} className="mr-2" /> Compare
          </Button>
        </div>
      )}
    </div>
  );
}
